import React, { useEffect, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { CalendarRange, Loader2, TrendingUp } from 'lucide-react';
import { tenantAdminApi } from '@/utils/tenantAdminApi';
import { IITH } from '@/pages/landing/iithBrand';

const PERIODS = [
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'quarter', label: 'Quarter' },
  { id: 'year', label: 'Year' },
];

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-white px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold text-zinc-800 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
}

/** Impressive appointment volume chart with week/month/quarter/year periods. */
export default function StaffAppointmentsChart({ userId }) {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setLoading(true);
    tenantAdminApi.getUserAppointments(userId, period)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, period]);

  const series = data?.series || [];
  const tickEvery = period === 'year' ? 14 : period === 'quarter' ? 7 : 1;

  return (
    <section
      className="rounded-2xl border bg-white shadow-sm overflow-hidden"
      data-testid="staff-appointments-chart"
    >
      <div
        className="px-4 py-3 flex flex-wrap items-center justify-between gap-3"
        style={{ background: `linear-gradient(120deg, ${IITH.navy}, ${IITH.navyMid})` }}
      >
        <div className="text-white">
          <p className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: IITH.gold }}>
            Clinical activity
          </p>
          <h3 className="text-sm font-semibold flex items-center gap-2 mt-0.5">
            <CalendarRange className="h-4 w-4" /> Appointments by period
          </h3>
        </div>
        <div className="flex gap-1 bg-white/10 rounded-full p-0.5">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold transition ${
                period === p.id ? 'bg-white text-[#071B34]' : 'text-white/80 hover:bg-white/10'
              }`}
              data-testid={`appt-period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#E8EEF5] border-b">
        {[
          ['Total', data?.total ?? '—'],
          ['Avg / day', data?.avg_per_day ?? '—'],
          ['Active days', data?.active_days ?? '—'],
          ['Peak day', data?.peak?.count ? `${data.peak.count} · ${data.peak.label}` : '—'],
        ].map(([label, value]) => (
          <div key={label} className="bg-white px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-wide text-zinc-400 font-medium">{label}</p>
            <p className="text-sm font-semibold text-zinc-900 tabular-nums mt-0.5 truncate">{value}</p>
          </div>
        ))}
      </div>

      <div className="p-4 h-64">
        {loading ? (
          <div className="h-full grid place-items-center text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : series.length === 0 ? (
          <div className="h-full grid place-items-center text-sm text-zinc-500">
            No appointment data for this period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8EEF5" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: '#9AADBE' }}
                interval={tickEvery - 1}
                axisLine={false}
                tickLine={false}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#9AADBE' }} axisLine={false} tickLine={false} />
              <Tooltip content={<Tip />} cursor={{ fill: 'rgba(241,90,34,0.06)' }} />
              <Bar dataKey="appointments" name="Appointments" fill={IITH.saffron} radius={[4, 4, 0, 0]} maxBarSize={28} />
              {data?.metric === 'appointments' && (
                <Bar dataKey="visits" name="Visits" fill={IITH.navy} radius={[4, 4, 0, 0]} maxBarSize={28} />
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="px-4 pb-3 flex items-center gap-1.5 text-[11px] text-zinc-500">
        <TrendingUp className="h-3.5 w-3.5" style={{ color: IITH.saffron }} />
        {data?.metric === 'appointments'
          ? 'Saffron = queue appointments · Navy = completed consultations'
          : 'Showing consultation visits (no queue rows in this period)'}
      </div>
    </section>
  );
}
